import { StorageUtil } from '../../../shared/utils/storage';
import { ColumnField } from '../components/table/column-groups';
import {
  LEAD_COMPANY_PROFILE_MODAL_SIZE,
  LEAD_PROFILE_MODAL_SIZE,
} from '../helpers/helper';

const STORAGE_KEY = 'v2_lead_finder_column_preferences';

export interface ColumnPreferences {
  selectedFields: ColumnField[];
  columnSizes?: Record<string, number>;
  timestamp: number;
  userId?: string | number;
}

/**
 * Save column preferences to storage
 * @param selectedFields - Array of column fields with optional size property
 * @param userId - Optional user ID for multi-user support
 * @param columnSizes - Optional separate column sizes map (for backward compatibility)
 */
export const saveColumnPreferences = (
  selectedFields: ColumnField[],
  userId?: string | number,
  columnSizes?: Record<string, number>,
): void => {
  try {
    // If columnSizes map is provided separately, merge sizes into fields
    const fieldsWithSizes = columnSizes
      ? selectedFields.map((field) => ({
          ...field,
          size: columnSizes[String(field.id)] ?? field.size,
        }))
      : selectedFields;

    const preferences: ColumnPreferences = {
      selectedFields: fieldsWithSizes,
      columnSizes,
      timestamp: Date.now(),
      userId,
    };
    StorageUtil.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    // Silently fail if storage is not available
  }
};

/**
 * Load column preferences from storage
 */
export const loadColumnPreferences = (
  userId?: string | number,
): ColumnField[] | null => {
  try {
    const stored = StorageUtil.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const preferences: ColumnPreferences = JSON.parse(stored);

    // If userId is provided, check if preferences match the current user
    if (userId && preferences.userId && preferences.userId !== userId) {
      return null;
    }

    return preferences.selectedFields;
  } catch (error) {
    return null;
  }
};

/**
 * Clear column preferences from storage
 */
export const clearColumnPreferences = (): void => {
  try {
    StorageUtil.removeItem(STORAGE_KEY);
  } catch (error) {
    // Silently fail if storage is not available
  }
};

/**
 * Check if stored preferences exist
 */
export const hasColumnPreferences = (): boolean => {
  try {
    return StorageUtil.getItem(STORAGE_KEY) !== null;
  } catch (error) {
    return false;
  }
};

/**
 * Save only column sizes without affecting other preferences
 * This is useful for updating sizes during column resize operations
 */
export const saveColumnSizes = (columnSizes: Record<string, number>): void => {
  try {
    const stored = StorageUtil.getItem(STORAGE_KEY);
    if (!stored) {
      // If no preferences exist, create new ones with just sizes
      const preferences: ColumnPreferences = {
        selectedFields: [],
        columnSizes,
        timestamp: Date.now(),
      };
      StorageUtil.setItem(STORAGE_KEY, JSON.stringify(preferences));
      return;
    }

    const preferences: ColumnPreferences = JSON.parse(stored);

    // Update sizes in selectedFields
    const updatedFields = preferences.selectedFields.map((field) => ({
      ...field,
      size: columnSizes[String(field.id)] ?? field.size,
    }));

    const updatedPreferences: ColumnPreferences = {
      ...preferences,
      selectedFields: updatedFields,
      columnSizes: {
        ...preferences.columnSizes,
        ...columnSizes,
      },
      timestamp: Date.now(),
    };

    StorageUtil.setItem(STORAGE_KEY, JSON.stringify(updatedPreferences));
  } catch (error) {
    // Silently fail if storage is not available
  }
};

/**
 * Get saved column sizes from storage
 */
export const getColumnSizes = (): Record<string, number> | null => {
  try {
    const stored = StorageUtil.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const preferences: ColumnPreferences = JSON.parse(stored);

    // Build sizes map from selectedFields (which contain sizes)
    // and from columnSizes (for backward compatibility)
    const sizesFromFields: Record<string, number> = {};
    preferences.selectedFields.forEach((field) => {
      if (field.size) {
        sizesFromFields[String(field.id)] = field.size;
      }
    });

    return {
      ...sizesFromFields,
      ...preferences.columnSizes,
    };
  } catch (error) {
    return null;
  }
};

export const clearLeadProfileModalSize = () =>
  localStorage.removeItem(LEAD_PROFILE_MODAL_SIZE);

export const clearLeadCompanyProfileModalSize = () =>
  localStorage.removeItem(LEAD_COMPANY_PROFILE_MODAL_SIZE);
